import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, alpha } from '@mui/material';
import { Timeline } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { useTranslation } from 'react-i18next';

const ExposureTimeline = ({ history }) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('exposure');

  const chartData = history.map(item => ({
    date: new Date(item.trade_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    exposure: (item.exposure || 0) * 100,
    baseExposure: (item.base_exposure || 0) * 100,
    cap: (item.exposure_cap || 0) * 100,
    dr10: (item.down_risk_10 || 0) * 100,
    dr20: (item.down_risk_20 || 0) * 100,
    dr60: (item.down_risk_60 || 0) * 100,
    drawdown: (item.drawdown || 0) * 100,
  }));

  const handleViewChange = (event, newView) => {
    if (newView !== null) setViewMode(newView);
  };

  const renderChart = () => {
    if (chartData.length === 0) {
      return (
        <Box 
          sx={{ 
            height: 180, 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            bgcolor: alpha('#8b5cf6', 0.02),
            border: '1px dashed',
            borderColor: alpha('#8b5cf6', 0.15),
            borderRadius: 1.5,
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha('#8b5cf6', 0.08),
              color: alpha('#8b5cf6', 0.6),
            }}
          >
            <Timeline sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', mb: 0.25 }}>
              {t('risk.noDataAvailable')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
              {t('risk.noDataDescription')}
            </Typography>
          </Box>
        </Box>
      );
    }

    const commonProps = {
      data: chartData,
      margin: { top: 5, right: 5, left: -15, bottom: 0 },
    };

    const commonAxisProps = {
      tick: { fontSize: 9, fill: '#9ca3af' },
      tickLine: false,
      axisLine: false,
    };

    switch (viewMode) {
      case 'exposure':
        return (
          <ResponsiveContainer width="100%" height={180}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.04)} vertical={false} />
              <XAxis dataKey="date" {...commonAxisProps} height={20} />
              <YAxis {...commonAxisProps} width={35} />
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.6875rem', padding: '4px 8px' }}
                formatter={(value) => value.toFixed(1) + '%'}
              />
              <Legend wrapperStyle={{ fontSize: '10px' }} iconSize={10} />
              <ReferenceLine y={70} stroke="#10b981" strokeDasharray="2 2" strokeWidth={1} />
              <Line type="monotone" dataKey="baseExposure" stroke="#3b82f6" strokeWidth={1.5} dot={false} name="Base" />
              <Line type="monotone" dataKey="cap" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="2 2" dot={false} name="Cap" />
              <Line type="monotone" dataKey="exposure" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} name="Active" />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'downrisk':
        return (
          <ResponsiveContainer width="100%" height={180}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.04)} vertical={false} />
              <XAxis dataKey="date" {...commonAxisProps} height={20} />
              <YAxis {...commonAxisProps} width={35} />
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.6875rem', padding: '4px 8px' }}
                formatter={(value) => value.toFixed(1) + '%'}
              />
              <Legend wrapperStyle={{ fontSize: '10px' }} iconSize={10} />
              <ReferenceLine y={50} stroke="#ef4444" strokeDasharray="2 2" strokeWidth={1} />
              <Line type="monotone" dataKey="dr10" stroke="#ef4444" strokeWidth={1.5} dot={false} name="DR10" />
              <Line type="monotone" dataKey="dr20" stroke="#f59e0b" strokeWidth={1.5} dot={false} name="DR20" />
              <Line type="monotone" dataKey="dr60" stroke="#8b5cf6" strokeWidth={1.5} dot={false} name="DR60" />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'combined':
        return (
          <ResponsiveContainer width="100%" height={180}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.04)} vertical={false} />
              <XAxis dataKey="date" {...commonAxisProps} height={20} />
              <YAxis {...commonAxisProps} yAxisId="left" width={35} />
              <YAxis {...commonAxisProps} yAxisId="right" orientation="right" width={35} />
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.6875rem', padding: '4px 8px' }}
                formatter={(value) => value.toFixed(1) + '%'}
              />
              <Legend wrapperStyle={{ fontSize: '10px' }} iconSize={10} />
              <Line type="monotone" dataKey="exposure" stroke="#10b981" strokeWidth={2} yAxisId="left" dot={{ r: 2 }} name="Exposure" />
              <Line type="monotone" dataKey="dr20" stroke="#f59e0b" strokeWidth={1.5} yAxisId="right" dot={false} name="DR20" />
              <Line type="monotone" dataKey="drawdown" stroke="#ef4444" strokeWidth={1.5} yAxisId="left" strokeDasharray="2 2" dot={false} name="DD" />
            </LineChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        background: '#ffffff',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header - Ultra Compact */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#8b5cf6', 0.1),
                color: '#8b5cf6',
              }}
            >
              <Timeline sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.2, mb: 0.25 }}>
                {t('risk.historicalTimeline')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', lineHeight: 1 }}>
                {t('risk.last30Days')}
              </Typography>
            </Box>
          </Box>

          {/* View Tabs - Minimal Style */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[
              { value: 'exposure', label: t('risk.exposure') },
              { value: 'downrisk', label: 'DownRisk' },
              { value: 'combined', label: t('common.all') },
            ].map((tab) => (
              <Box
                key={tab.value}
                onClick={() => handleViewChange(null, tab.value)}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  cursor: 'pointer',
                  borderRadius: 1,
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  ...(viewMode === tab.value ? {
                    color: '#8b5cf6',
                    bgcolor: alpha('#8b5cf6', 0.08),
                  } : {
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'text.primary',
                      bgcolor: alpha('#000', 0.04),
                    },
                  }),
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.6875rem',
                    fontWeight: viewMode === tab.value ? 700 : 600,
                    letterSpacing: 0.25,
                    lineHeight: 1,
                  }}
                >
                  {tab.label}
                </Typography>
                {viewMode === tab.value && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -2,
                      left: 0,
                      right: 0,
                      height: 2,
                      bgcolor: '#8b5cf6',
                      borderRadius: '2px 2px 0 0',
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Chart */}
        {renderChart()}
      </CardContent>
    </Card>
  );
};

export default ExposureTimeline;
